/**
 * Writer execution plan — same model as the Content Brief PDF, rendered in the UI.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  ChevronDown,
  ClipboardList,
  Copy,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Spinner } from '@/components/spinner';
import { Button } from '@/components/ui/button';
import {
  Section,
  SectionDescription,
  SectionHeader,
  SectionTitle,
} from '@/components/ui/section';
import { Surface } from '@/components/ui/surface';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import type { BriefInsights, CtaBlock } from '../insights-types';

export function WritingPlanPanel({
  data,
  loading,
  error,
}: {
  data: BriefInsights | undefined;
  loading: boolean;
  error: Error | null;
}): JSX.Element {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <Section>
        <p className="text-sm text-destructive">
          Couldn’t load the writing plan. Try refreshing, or export the PDF.
        </p>
      </Section>
    );
  }
  if (!data) return <></>;

  const copyPrompt = async (): Promise<void> => {
    if (!data.ai_prompt) return;
    try {
      await navigator.clipboard.writeText(data.ai_prompt);
      setCopied(true);
      toast({ title: 'AI prompt copied' });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const hasKeywordStrategy =
    data.keyword_strategy &&
    (data.keyword_strategy.primary ||
      data.keyword_strategy.secondary.length > 0 ||
      data.keyword_strategy.semantic.length > 0 ||
      data.keyword_strategy.avoid.length > 0);

  return (
    <div className="space-y-4">
      {(data.warnings ?? []).length > 0 ? (
        <Surface
          tone="muted"
          className="border-l-4 border-l-amber-500 bg-amber-50 p-4 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <div className="text-xs font-semibold uppercase tracking-wider">
            This brief was completed with warnings
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {data.warnings.map((w, i) => (
              <li
                key={i}
                className={w.severity === 'error' ? 'text-red-700 dark:text-red-300' : ''}
              >
                <span className="mr-2 inline-block rounded bg-black/5 px-1.5 py-0.5 font-mono text-[10px] dark:bg-white/10">
                  {w.code}
                </span>
                {w.message}
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      {data.based_on_strategy ? (
        <Surface tone="muted" className="border-l-4 border-l-primary p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Based on Competitor Analysis
          </div>
          <p className="mt-1 text-sm leading-relaxed">{data.based_on_strategy}</p>
        </Surface>
      ) : null}

      {data.competitors_to_beat.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Competitors to beat</SectionTitle>
              <SectionDescription>
                URLs currently ranking that this article needs to outrank.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="flex flex-wrap gap-2">
            {data.competitors_to_beat.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noreferrer noopener"
                className="group inline-flex max-w-full items-center gap-2 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/5"
                title={c.unique_angle ?? c.url}
              >
                <span className="truncate max-w-[280px]">{stripUrl(c.url)}</span>
                {c.word_count != null ? (
                  <span className="tabular font-mono text-[10px] text-muted-foreground">
                    {c.word_count.toLocaleString()}w
                  </span>
                ) : null}
                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground group-hover:text-primary" />
              </a>
            ))}
          </div>
        </Section>
      ) : null}

      {data.content_goal ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Content goal</SectionTitle>
            </div>
          </SectionHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <GoalCell label="Goal" value={data.content_goal.goal} />
            {data.content_goal.format ? (
              <GoalCell label="Format" value={data.content_goal.format} />
            ) : null}
            {data.content_goal.differentiator ? (
              <GoalCell
                label="Differentiator"
                value={data.content_goal.differentiator}
              />
            ) : null}
            {data.content_goal.unique_value_prop ? (
              <GoalCell
                label="Unique value prop"
                value={data.content_goal.unique_value_prop}
              />
            ) : null}
          </div>
        </Section>
      ) : null}

      {data.intent ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Search intent</SectionTitle>
              <SectionDescription>
                What readers are looking for and where they are in the buying journey.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            {data.intent.primary ? (
              <GoalCell label="Primary" value={data.intent.primary} capitalize />
            ) : null}
            {data.intent.secondary ? (
              <GoalCell label="Secondary" value={data.intent.secondary} capitalize />
            ) : null}
            {data.intent.funnel_stage ? (
              <GoalCell
                label="Funnel stage"
                value={data.intent.funnel_stage}
                capitalize
              />
            ) : null}
          </div>
          {data.intent.reasoning ? (
            <p className="mt-3 border-t border-border/50 pt-3 text-sm leading-relaxed text-muted-foreground">
              {data.intent.reasoning}
            </p>
          ) : null}
          {data.serp_features && data.serp_features.length > 0 ? (
            <div className="mt-3 border-t border-border/50 pt-3">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                Targeting SERP features
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.serp_features.map((f) => (
                  <span
                    key={f}
                    className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-foreground/90"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </Section>
      ) : data.serp_features && data.serp_features.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>SERP features targeted</SectionTitle>
            </div>
          </SectionHeader>
          <div className="flex flex-wrap gap-1.5">
            {data.serp_features.map((f) => (
              <span
                key={f}
                className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-foreground/90"
              >
                {f}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {data.persona ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Persona</SectionTitle>
              <SectionDescription>
                {[data.persona.name, data.persona.role].filter(Boolean).join(' · ') ||
                  'Who this article is for.'}
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="grid gap-4 sm:grid-cols-3">
            {data.persona.pain_points.length > 0 ? (
              <BulletList label="Pain points" items={data.persona.pain_points} />
            ) : null}
            {data.persona.goals.length > 0 ? (
              <BulletList label="Goals" items={data.persona.goals} />
            ) : null}
            {data.persona.objections.length > 0 ? (
              <BulletList label="Objections" items={data.persona.objections} />
            ) : null}
          </div>
        </Section>
      ) : null}

      {data.structure_targets ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Structure targets</SectionTitle>
            </div>
          </SectionHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            {data.structure_targets.word_count_range ? (
              <GoalCell
                label="Word count"
                value={`${data.structure_targets.word_count_range} words`}
              />
            ) : null}
            {data.structure_targets.h2_count != null ? (
              <GoalCell
                label="H2 sections"
                value={String(data.structure_targets.h2_count)}
              />
            ) : null}
            {data.structure_targets.reading_time_minutes != null ? (
              <GoalCell
                label="Reading time"
                value={`${data.structure_targets.reading_time_minutes} min`}
              />
            ) : null}
          </div>
        </Section>
      ) : null}

      {data.ai_prompt ? (
        <Section>
          <SectionHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SectionTitle className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI execution prompt
                </SectionTitle>
                <SectionDescription>
                  {data.ai_prompt_from_competitor
                    ? 'Inherited from Competitor Analysis — paste into your writing tool.'
                    : 'Paste into your writing tool to draft this article.'}
                </SectionDescription>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => void copyPrompt()}>
                {copied ? (
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                ) : (
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </SectionHeader>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-xs leading-relaxed">
            {data.ai_prompt}
          </pre>
        </Section>
      ) : null}

      {data.searcher_wants ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Searcher wants</SectionTitle>
            </div>
          </SectionHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Wants
              </div>
              <ul className="space-y-1.5">
                {(data.searcher_wants.wants ?? []).map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Doesn’t want
              </div>
              <ul className="space-y-1.5">
                {(data.searcher_wants.does_not_want ?? []).map((w) => (
                  <li key={w} className="text-sm text-muted-foreground">
                    · {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      ) : null}

      {hasKeywordStrategy ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Keyword strategy</SectionTitle>
              <SectionDescription>
                Primary, secondary, semantic, and terms to avoid.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="space-y-3">
            {data.keyword_strategy!.primary ? (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Primary
                </div>
                <span className="inline-flex rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  {data.keyword_strategy!.primary}
                </span>
              </div>
            ) : null}
            {data.keyword_strategy!.secondary.length > 0 ? (
              <KeywordPillGroup
                label="Secondary"
                items={data.keyword_strategy!.secondary}
                tone="neutral"
              />
            ) : null}
            {data.keyword_strategy!.semantic.length > 0 ? (
              <KeywordPillGroup
                label="Semantic"
                items={data.keyword_strategy!.semantic}
                tone="muted"
              />
            ) : null}
            {data.keyword_strategy!.avoid.length > 0 ? (
              <KeywordPillGroup
                label="Avoid"
                items={data.keyword_strategy!.avoid}
                tone="warn"
              />
            ) : null}
          </div>
        </Section>
      ) : null}

      {(data.keyword_placement ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Keyword placement</SectionTitle>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Keyword', 'Priority', 'Where to use']}
            rows={data.keyword_placement.map((k) => [
              k.keyword,
              k.priority,
              k.where_to_use,
            ])}
          />
        </Section>
      ) : null}

      {(data.section_briefs ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Section briefs</SectionTitle>
              <SectionDescription>
                Talking points, H3s, and evidence references for each H2.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="space-y-3">
            {data.section_briefs.map((s, idx) => (
              <Surface key={`${s.heading}-${idx}`} tone="muted" className="p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">{s.heading}</h3>
                    {s.priority ? (
                      <span className={cn('rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide', priorityToneClass(s.priority))}>
                        {s.priority}
                      </span>
                    ) : null}
                    {s.intent ? (
                      <span className="rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                        {s.intent}
                      </span>
                    ) : null}
                  </div>
                  {s.target_words ? (
                    <span className="text-xs text-muted-foreground">{s.target_words}</span>
                  ) : null}
                </div>
                {s.talking_points.length > 0 ? (
                  <ul className="mt-2 space-y-1 pl-1 text-sm">
                    {s.talking_points.map((tp, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-muted-foreground"
                      >
                        <span className="mt-0.5 text-primary">·</span>
                        <span>{tp}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {s.h3s.length > 0 ? (
                  <ul className="mt-3 space-y-1.5 border-t border-border/50 pt-3">
                    {s.h3s.map((h3, i) => (
                      <li
                        key={`${h3}-${i}`}
                        className="flex items-center gap-2 rounded-md bg-background/40 px-3 py-1.5 text-sm text-foreground/90 ring-1 ring-border/40"
                      >
                        <span className="font-mono text-[10px] text-muted-foreground">
                          H3
                        </span>
                        <span className="truncate">{h3}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {s.evidence_refs.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.evidence_refs.map((id) => (
                      <span
                        key={id}
                        className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                      >
                        {id}
                      </span>
                    ))}
                  </div>
                ) : null}
              </Surface>
            ))}
          </div>
        </Section>
      ) : null}

      {data.entities_to_cover.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Entities to cover</SectionTitle>
              <SectionDescription>
                Topics and terms that should appear in the article.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="flex flex-wrap gap-1.5">
            {data.entities_to_cover.map((e) => (
              <span
                key={e}
                className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground/90"
              >
                {e}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {data.comparison_tables && data.comparison_tables.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Comparison tables to build</SectionTitle>
              <SectionDescription>
                Column headers with empty rows for the writer to fill.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="space-y-4">
            {data.comparison_tables.map((t, i) => (
              <Surface key={`${t.topic}-${i}`} tone="muted" className="p-4">
                <h3 className="mb-2 text-sm font-semibold">{t.topic}</h3>
                {t.columns.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[420px] border-collapse text-left text-sm">
                      <thead>
                        <tr className="border-b border-border/60 text-xs text-muted-foreground">
                          {t.columns.map((c) => (
                            <th key={c} className="py-2 pr-3 font-medium">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {t.row_hints.map((row, ri) => (
                          <tr key={ri} className="border-b border-border/40">
                            <td className="py-2 pr-3 font-medium">{row}</td>
                            {t.columns.slice(1).map((_, ci) => (
                              <td key={ci} className="py-2 pr-3 text-muted-foreground">
                                &nbsp;
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {t.comparison_set_hint ? (
                  <div className="mt-2 text-xs italic text-muted-foreground">
                    {t.comparison_set_hint}
                  </div>
                ) : null}
              </Surface>
            ))}
          </div>
        </Section>
      ) : null}

      {data.video_spec ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Video: produce or embed</SectionTitle>
            </div>
          </SectionHeader>
          <Surface tone="muted" className="p-4">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold">{data.video_spec.topic}</h3>
              {data.video_spec.produce_or_embed ? (
                <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {data.video_spec.produce_or_embed}
                </span>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {data.video_spec.target_length_minutes != null ? (
                <GoalCell
                  label="Length"
                  value={`~${data.video_spec.target_length_minutes} min`}
                />
              ) : null}
              {data.video_spec.hosting_hint ? (
                <GoalCell label="Hosting" value={data.video_spec.hosting_hint} />
              ) : null}
            </div>
            {data.video_spec.script_outline_bullets.length > 0 ? (
              <div className="mt-3">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Script outline
                </div>
                <ol className="space-y-1 pl-5 text-sm text-muted-foreground">
                  {data.video_spec.script_outline_bullets.map((b, i) => (
                    <li key={i} className="list-decimal">
                      {b}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </Surface>
        </Section>
      ) : null}

      {data.coverage_matrix && data.coverage_matrix.rows.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Competitor coverage matrix</SectionTitle>
              <SectionDescription>
                Topic × competitor grid — gaps and opportunities at a glance.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left font-medium">Topic</th>
                  {data.coverage_matrix.competitor_urls.map((u) => (
                    <th
                      key={u}
                      className="py-2 pr-3 text-center font-medium"
                      title={u}
                    >
                      <span className="max-w-[120px] truncate inline-block align-middle">
                        {stripUrl(u)}
                      </span>
                    </th>
                  ))}
                  <th className="bg-primary/5 py-2 pr-3 text-center font-medium">Us</th>
                </tr>
              </thead>
              <tbody>
                {data.coverage_matrix.rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/40">
                    <td className="py-2 pr-3 text-left">{r.topic}</td>
                    {r.cells.map((cell, ci) => (
                      <td
                        key={ci}
                        className={cn(
                          'py-2 pr-3 text-center font-mono text-sm',
                          cell
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : 'text-red-700 dark:text-red-400',
                        )}
                      >
                        {cell ? '✓' : '✗'}
                      </td>
                    ))}
                    <td
                      className={cn(
                        'bg-primary/5 py-2 pr-3 text-center font-mono text-sm',
                        r.us
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-red-700 dark:text-red-400',
                      )}
                    >
                      {r.us ? '✓' : '✗'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {data.faqs.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>FAQs</SectionTitle>
              <SectionDescription>
                {data.faqs.length} FAQ{data.faqs.length === 1 ? '' : 's'} to answer.
              </SectionDescription>
            </div>
          </SectionHeader>
          <ol className="space-y-2">
            {data.faqs.map((f, i) => (
              <li
                key={i}
                className="rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {f.question}
                  </span>
                  {f.intent ? (
                    <span className="rounded-full border border-border/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {f.intent}
                    </span>
                  ) : null}
                </div>
                {f.answer ? (
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                    {f.answer}
                  </p>
                ) : null}
                {f.attached_to_section_h2 ? (
                  <p className="mt-1 text-[11px] italic text-muted-foreground">
                    Best placed inline in section: {f.attached_to_section_h2}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </Section>
      ) : null}

      {data.meta_titles.length > 0 || data.meta_descriptions.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Titles & meta descriptions</SectionTitle>
              <SectionDescription>
                Character counts help you keep within Google’s SERP limits.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="grid gap-4 lg:grid-cols-2">
            {data.meta_titles.length > 0 ? (
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Page title options
                </div>
                <ol className="space-y-2">
                  {data.meta_titles.map((t, i) => (
                    <MetaVariantItem
                      key={i}
                      index={i}
                      text={t.value}
                      chars={t.char_count}
                      greenMax={60}
                      amberMax={70}
                    />
                  ))}
                </ol>
              </div>
            ) : null}
            {data.meta_descriptions.length > 0 ? (
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Search snippet options
                </div>
                <ol className="space-y-2">
                  {data.meta_descriptions.map((d, i) => (
                    <MetaVariantItem
                      key={i}
                      index={i}
                      text={d.value}
                      chars={d.char_count}
                      greenMin={140}
                      greenMax={160}
                      amberMax={175}
                      multiline
                    />
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        </Section>
      ) : null}

      {data.url_slugs.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>URL slug options</SectionTitle>
            </div>
          </SectionHeader>
          <ol className="space-y-2">
            {data.url_slugs.map((s, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-md border border-border/60 bg-background/40 p-2"
              >
                <span className="tabular flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-sm text-foreground/90">
                  /{s}
                </span>
              </li>
            ))}
          </ol>
        </Section>
      ) : null}

      {data.ctas.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>CTAs</SectionTitle>
              <SectionDescription>
                Ready-to-paste calls-to-action grouped by placement.
              </SectionDescription>
            </div>
          </SectionHeader>
          {data.cta_legend && data.cta_legend.length > 0 ? (
            <Surface tone="muted" className="mb-4 p-3">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Placement guide
              </div>
              <ul className="space-y-1 text-xs text-foreground/90">
                {data.cta_legend.map((l) => (
                  <li key={l.placement}>
                    <span className="mr-2 inline-block min-w-[130px] font-semibold">
                      {l.label}
                    </span>
                    <span className="text-muted-foreground">{l.description}</span>
                  </li>
                ))}
              </ul>
            </Surface>
          ) : null}
          {data.cta_groups && data.cta_groups.length > 0 ? (
            <div className="space-y-4">
              {data.cta_groups.map((g) => (
                <div key={g.placement}>
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.placement_label}
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {g.ctas.map((c, i) => (
                      <CtaCard key={i} cta={c} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {data.ctas.map((c, i) => (
                <CtaCard key={i} cta={c} />
              ))}
            </div>
          )}
        </Section>
      ) : null}

      {data.media_recommendations.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Media recommendations</SectionTitle>
              <SectionDescription>
                Images and videos to plan for — including alt text suggestions.
              </SectionDescription>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Type', 'Placement', 'Purpose', 'Alt text']}
            rows={data.media_recommendations.map((m) => [
              m.type,
              m.placement ?? '—',
              m.purpose ?? '—',
              m.alt_text ?? '—',
            ])}
          />
        </Section>
      ) : null}

      {data.external_sources.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>External sources to reference</SectionTitle>
            </div>
          </SectionHeader>
          <ul className="space-y-2">
            {data.external_sources.map((s, i) => (
              <li
                key={i}
                className="rounded-md border border-border/60 bg-card p-3"
              >
                <div className="flex items-baseline gap-2">
                  <span className="flex-1 text-sm font-medium">{s.label}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                    suggested
                  </span>
                </div>
                {s.url_hint ? (
                  <div className="mt-1 text-xs text-muted-foreground">{s.url_hint}</div>
                ) : null}
                {s.why ? <div className="mt-1 text-sm">{s.why}</div> : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {data.eeat_requirements || data.eeat_notes ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>E-E-A-T requirements</SectionTitle>
              <SectionDescription>
                Trust, expertise, and authorship signals to include.
              </SectionDescription>
            </div>
          </SectionHeader>
          {data.eeat_requirements ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.eeat_requirements.byline_required != null ? (
                <GoalCell
                  label="Byline required"
                  value={data.eeat_requirements.byline_required ? 'Yes' : 'No'}
                />
              ) : null}
              {data.eeat_requirements.author_credentials ? (
                <GoalCell
                  label="Author credentials"
                  value={data.eeat_requirements.author_credentials}
                />
              ) : null}
              {data.eeat_requirements.first_person_expected != null ? (
                <GoalCell
                  label="First-person voice"
                  value={
                    data.eeat_requirements.first_person_expected ? 'Use it' : 'Avoid it'
                  }
                />
              ) : null}
              {data.eeat_requirements.citations_expected != null ? (
                <GoalCell
                  label="Citations expected"
                  value={String(data.eeat_requirements.citations_expected)}
                />
              ) : null}
              {data.eeat_requirements.review_by ? (
                <GoalCell
                  label="Review by"
                  value={data.eeat_requirements.review_by}
                />
              ) : null}
              {data.eeat_requirements.original_updated_date_policy ? (
                <GoalCell
                  label="Update policy"
                  value={data.eeat_requirements.original_updated_date_policy}
                />
              ) : null}
              {data.eeat_requirements.methodology_note_required ? (
                <GoalCell label="Methodology note" value="Required" />
              ) : null}
              {data.eeat_requirements.expert_quote_required ? (
                <GoalCell label="Expert quote" value="Required" />
              ) : null}
              {data.eeat_requirements.first_hand_experience_asset ? (
                <GoalCell
                  label="First-hand asset"
                  value={data.eeat_requirements.first_hand_experience_asset}
                />
              ) : null}
            </div>
          ) : null}
          {data.eeat_notes ? (
            <p className="mt-3 border-t border-border/50 pt-3 text-sm leading-relaxed text-muted-foreground">
              {data.eeat_notes}
            </p>
          ) : null}
        </Section>
      ) : null}

      {data.schema_block ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Schema markup</SectionTitle>
              <SectionDescription>
                Structured data to help Google understand this article.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="space-y-3">
            {data.schema_block.primary ? (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Primary
                </div>
                <span className="inline-flex rounded-md bg-muted px-2.5 py-1 font-mono text-xs text-foreground/90">
                  {data.schema_block.primary}
                </span>
              </div>
            ) : null}
            {data.schema_block.additional.length > 0 ? (
              <div>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Additional
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {data.schema_block.additional.map((s) => (
                    <span
                      key={s}
                      className="rounded-md bg-muted px-2 py-0.5 font-mono text-[11px] text-foreground/90"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {data.schema_block.notes ? (
              <p className="text-sm leading-relaxed text-muted-foreground">
                {data.schema_block.notes}
              </p>
            ) : null}
          </div>
        </Section>
      ) : null}

      {data.competitor_benchmark.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Competitor benchmark</SectionTitle>
              <SectionDescription>
                How the top-ranking pages compare on angle, strengths, and weaknesses.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Page</th>
                  <th className="py-2 pr-3 text-right font-medium">Words</th>
                  <th className="py-2 pr-3 font-medium">Angle</th>
                  <th className="py-2 pr-3 font-medium">Strength</th>
                  <th className="py-2 pr-3 font-medium">Weakness</th>
                </tr>
              </thead>
              <tbody>
                {data.competitor_benchmark.map((r, i) => (
                  <tr key={i} className="border-b border-border/40 align-top">
                    <td className="py-2.5 pr-3 font-mono text-xs">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-foreground hover:underline"
                      >
                        <span className="max-w-[240px] truncate">
                          {stripUrl(r.url)}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </a>
                    </td>
                    <td className="tabular py-2.5 pr-3 text-right">
                      {r.word_count != null ? r.word_count.toLocaleString() : '—'}
                    </td>
                    <td className="py-2.5 pr-3 text-foreground/90">
                      {r.unique_angle ?? '—'}
                    </td>
                    <td className="py-2.5 pr-3 text-emerald-700 dark:text-emerald-400">
                      {r.strength ?? '—'}
                    </td>
                    <td className="py-2.5 pr-3 text-amber-700 dark:text-amber-400">
                      {r.weakness ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {data.gaps_to_fill.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Gaps to fill</SectionTitle>
              <SectionDescription>
                What competitors missed — cover these to win the ranking.
              </SectionDescription>
            </div>
          </SectionHeader>
          <ul className="space-y-2 text-sm">
            {data.gaps_to_fill.map((g, i) => (
              <li
                key={i}
                className="rounded-md border border-border/60 bg-card p-2"
              >
                <div className="flex items-baseline gap-2">
                  {g.gap_type ? (
                    <span className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {g.gap_type}
                    </span>
                  ) : null}
                  <span className="font-medium">{g.title}</span>
                </div>
                {g.why_matters ? (
                  <div className="mt-1 text-muted-foreground">{g.why_matters}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {(data.writing_checklist ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                Writing checklist
              </SectionTitle>
            </div>
          </SectionHeader>
          <ul className="space-y-1.5">
            {data.writing_checklist.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border" />
                {c}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {data.publish_checklist_groups.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                Publish checklist
                {data.refresh_cadence ? (
                  <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Refresh: {data.refresh_cadence}
                  </span>
                ) : null}
              </SectionTitle>
              <SectionDescription>
                Grouped by technical, editorial, and distribution steps.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="space-y-4">
            {data.publish_checklist_groups.map((g) => (
              <div key={g.category}>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {g.category}
                </div>
                <ul className="space-y-1 pl-1 text-sm">
                  {g.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-muted-foreground"
                    >
                      <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {data.internal_links.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Internal links</SectionTitle>
              <SectionDescription>
                Add these links inside the article so readers can find related pages.
              </SectionDescription>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Path', 'Anchor text']}
            rows={data.internal_links.map((l) => [l.path, l.anchor])}
          />
        </Section>
      ) : null}

      {data.top_risks.length > 0 ? (
        <RisksAndNotes risks={data.top_risks} />
      ) : null}
    </div>
  );
}

function RisksAndNotes({ risks }: { risks: string[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Section tone="muted">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-foreground">
            Risks & notes
          </span>
          <span className="text-xs text-muted-foreground">({risks.length})</span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open ? (
        <ul className="mt-3 space-y-1.5 border-t border-border/50 pt-3 text-sm">
          {risks.map((r, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-muted-foreground"
            >
              <span className="mt-0.5 text-destructive">·</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

function GoalCell({
  label,
  value,
  capitalize = false,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
}): JSX.Element {
  return (
    <Surface tone="muted" className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 text-sm leading-relaxed',
          capitalize && 'capitalize',
        )}
      >
        {value}
      </div>
    </Surface>
  );
}

function BulletList({
  label,
  items,
}: {
  label: string;
  items: string[];
}): JSX.Element {
  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <ul className="space-y-1 pl-1 text-sm">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-muted-foreground">
            <span className="mt-0.5 text-primary">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function KeywordPillGroup({
  label,
  items,
  tone,
}: {
  label: string;
  items: string[];
  tone: 'neutral' | 'muted' | 'warn';
}): JSX.Element {
  const toneClass =
    tone === 'warn'
      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
      : tone === 'muted'
        ? 'border-border/60 bg-muted/60 text-muted-foreground'
        : 'border-border/60 bg-background/60 text-foreground/90';
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((k) => (
          <span
            key={k}
            className={cn('rounded-full border px-2.5 py-1 text-xs', toneClass)}
          >
            {k}
          </span>
        ))}
      </div>
    </div>
  );
}

function MetaVariantItem({
  index,
  text,
  chars,
  greenMin,
  greenMax,
  amberMax,
  multiline = false,
}: {
  index: number;
  text: string;
  chars: number;
  greenMin?: number;
  greenMax: number;
  amberMax: number;
  multiline?: boolean;
}): JSX.Element {
  const tone =
    chars > amberMax
      ? 'text-destructive'
      : chars > greenMax
        ? 'text-amber-600 dark:text-amber-400'
        : greenMin !== undefined && chars < greenMin
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-emerald-700 dark:text-emerald-400';
  return (
    <li className="rounded-md border border-border/60 bg-background/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="tabular flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
          {index + 1}
        </span>
        <span className={cn('tabular font-mono text-[11px]', tone)}>
          {chars} chars
        </span>
      </div>
      <p
        className={cn(
          'mt-2 text-sm text-foreground',
          multiline ? 'leading-relaxed' : 'truncate',
        )}
      >
        {text}
      </p>
    </li>
  );
}

function SimpleTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-left text-sm">
        <thead>
          <tr className="border-b border-border/60 text-xs text-muted-foreground">
            {headers.map((h) => (
              <th key={h} className="py-2 pr-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/40 align-top">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={cn(
                    'py-2.5 pr-3 leading-relaxed',
                    j === 0 && 'font-medium',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function stripUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function CtaCard({ cta }: { cta: CtaBlock }): JSX.Element {
  return (
    <div className="rounded-md border border-border/60 bg-card p-3">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          {cta.placement}
        </span>
      </div>
      {cta.headline ? (
        <div className="text-sm font-semibold">{cta.headline}</div>
      ) : null}
      {cta.body ? (
        <p className="mt-1 text-xs text-muted-foreground">{cta.body}</p>
      ) : null}
      {cta.action_label ? (
        <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
          {cta.action_label}
          <ArrowUpRight className="h-3 w-3" />
        </div>
      ) : null}
    </div>
  );
}

function priorityToneClass(priority: string): string {
  switch (priority) {
    case 'critical':
      return 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300';
    case 'recommended':
      return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'optional':
    default:
      return 'border-border/70 bg-muted/60 text-muted-foreground';
  }
}
